import { axiosInstance } from '@/api/mutator/custom-instance';
import type { ImportResult, ImportRowOutcome, ImportRowResult } from '@/types/excelImport';

/**
 * Domains that support the Excel round-trip. The value is the API path segment.
 */
export type ExcelEntity = 'customers' | 'objects' | 'tasks' | 'employees';

const FALLBACK_FILENAME: Record<ExcelEntity, string> = {
  customers: 'Kunden.xlsx',
  objects: 'Objekte.xlsx',
  tasks: 'Aufgaben.xlsx',
  employees: 'Mitarbeiter.xlsx',
};

/**
 * Downloads the round-trip workbook for an entity.
 *
 * `ids` narrows the file to the rows the user currently sees; omitting it exports
 * everything. The caller passes IDs rather than filter criteria because most list
 * filters are evaluated client-side — reconstructing them server-side would produce a
 * file that quietly disagrees with the screen.
 *
 * A POST, not a GET, because a filtered list can carry thousands of GUIDs — well past
 * what a URL holds. Goes through `axiosInstance` rather than the generated Orval client
 * because `customInstance` resolves to `response.data` and drops the headers, so a
 * generated client can never read `Content-Disposition`.
 */
export async function downloadExcelExport(
  entity: ExcelEntity,
  ids?: readonly string[],
): Promise<void> {
  const response = await axiosInstance.post(
    `/api/v1/${entity}/export/excel`,
    { ids: ids && ids.length > 0 ? ids : null },
    { responseType: 'blob' },
  );

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFromDisposition(
    response.headers['content-disposition'] as string | undefined,
    FALLBACK_FILENAME[entity],
  );
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Uploads an edited workbook.
 *
 * With `dryRun` the server validates and reports without writing anything, which is
 * what backs the preview step. Call again with `dryRun: false` to apply.
 *
 * `excludeRows` carries the rows deselected in the preview as `"{sheet}:{row}"` tokens.
 * A repeated form field, not a query parameter — 5,000 deselected rows would burst any
 * URL limit. The server reports each excluded row as Skipped / `imports.rowDeselected`.
 */
export async function uploadExcelImport(
  entity: ExcelEntity,
  file: File,
  dryRun: boolean,
  excludeRows?: readonly string[],
): Promise<ImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  excludeRows?.forEach((token) => formData.append('excludeRows', token));

  const response = await axiosInstance.post<ImportResult>(
    `/api/v1/${entity}/import/excel`,
    formData,
    {
      params: { dryRun },
      // The shared instance defaults to `application/json`. Axios only derives
      // `multipart/form-data; boundary=…` when no Content-Type is set, so the default
      // has to be cleared explicitly — otherwise the upload goes out as JSON and the
      // server answers 415.
      headers: { 'Content-Type': undefined },
    },
  );

  return { ...response.data, rows: response.data.rows.map(normalizeRow) };
}

/**
 * Index = the numeric value of `ImportRowOutcome` in
 * `FacilityFlow.Core/Common/Excel/ImportDtos.cs`. Both sides pin this order with a test.
 */
const KNOWN_OUTCOMES: ImportRowOutcome[] = ['Created', 'Updated', 'Skipped', 'Failed'];

/**
 * Resolves `outcome` to a canonical name so every row renders a readable result.
 *
 * Accepts three forms, because frontend and backend deploy separately and the report is
 * useless if a row shows a bare `2`:
 *   - `"Skipped"` — the current contract,
 *   - `"skipped"` — survives a future global JSON naming policy,
 *   - `2` — a backend build predating the string-enum converter.
 */
function normalizeRow(row: ImportRowResult): ImportRowResult {
  const raw = row.outcome as unknown;

  if (typeof raw === 'number' || /^\d+$/.test(String(raw))) {
    const byOrdinal = KNOWN_OUTCOMES[Number(raw)];
    return byOrdinal ? { ...row, outcome: byOrdinal } : row;
  }

  const byName = KNOWN_OUTCOMES.find(
    (known) => known.toLowerCase() === String(raw).toLowerCase(),
  );

  return byName ? { ...row, outcome: byName } : row;
}

/** True when the server sent an `outcome` this client cannot interpret. */
export function hasUnknownOutcomes(rows: ImportRowResult[]): boolean {
  return rows.some((row) => !KNOWN_OUTCOMES.includes(row.outcome));
}

/**
 * Reads the filename from a `Content-Disposition` header, handling both the plain
 * `filename="…"` form and the RFC 5987 `filename*=UTF-8''…` form used for umlauts.
 */
function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;

  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Malformed percent-encoding — fall through to the plain form.
    }
  }

  return disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? fallback;
}
